import React, { useState, useEffect, useCallback, type ReactNode } from "react";

import { type Theme, type ThemePreference, ThemeContext } from "./ThemeContext";

const STORAGE_KEY = "theme"; // values: "light" | "dark" | "system"

function readPreference(): ThemePreference {
  if (typeof window === "undefined") return "system";
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === "light" || saved === "dark" || saved === "system") return saved;
  // Legacy values were stored as just "light" or "dark"; treat anything else as system.
  return saved ? "system" : "system";
}

function resolvePreference(pref: ThemePreference): Theme {
  if (pref !== "system") return pref;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

interface Props {
  children: ReactNode;
}

export const ThemeProvider: React.FC<Props> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readPreference());
  const [theme, setTheme] = useState<Theme>(() => resolvePreference(preference));

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    localStorage.setItem(STORAGE_KEY, next);
    setTheme(resolvePreference(next));
  }, []);

  const toggleTheme = useCallback(() => {
    setPreference(theme === "light" ? "dark" : "light");
  }, [setPreference, theme]);

  // Apply theme class to <html>
  useEffect(() => {
    const root = document.documentElement;
    root.style.visibility = "hidden";
    requestAnimationFrame(() => {
      if (theme === "dark") {
        root.classList.add("dark");
        root.style.colorScheme = "dark";
      } else {
        root.classList.remove("dark");
        root.style.colorScheme = "light";
      }
      root.getBoundingClientRect();
      root.style.visibility = "";
    });
  }, [theme]);

  // Listen to OS changes only when preference is "system".
  useEffect(() => {
    if (preference !== "system") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setTheme(media.matches ? "dark" : "light");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [preference]);

  return (
    <ThemeContext.Provider value={{ theme, preference, setPreference, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
